import { Box, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getAccountUser, updateUser } from "../../api/account";
import { updateUserPassword } from "../../api/account/updateUserPassword";
import UserProfileForm from "../../components/Users/UserProfileForm";
import { setLoading } from "../../store/UI";
import { UpdateUserRequest, User } from "../../types/interfaces/user";
import { UpdateUserPasswordRequest } from "../../types/model/user";

const UserProfile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getSessionIds = () => {
    const session = JSON.parse(localStorage.getItem("session") || "{}");
    return {
      accountId: session.accountId ?? session.user?.accountId,
      userId: session.userId ?? session.user?.id,
    };
  };

  const { accountId, userId } = getSessionIds();

  useEffect(() => {
    if (!accountId || !userId) return;

    getAccountUser(accountId, userId).then(setUser);
  }, [accountId, userId]);

  const handleUpdate = async (values: UpdateUserRequest) => {
    if (!accountId || !userId) return false;

    setErrorMessage(null);
    dispatch(setLoading(true));
    try {
      await updateUser(accountId, userId, values);
      const data = await getAccountUser(accountId, userId);
      setUser(data);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          setErrorMessage(t("admin.users.errors.existing-user"));
        } else {
          setErrorMessage(t("generic.errors.something-went-wrong"));
        }
      } else {
        setErrorMessage(t("generic.errors.something-went-wrong"));
      }
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleUpdatePassword = async (values: UpdateUserPasswordRequest) => {
    if (!accountId || !userId) return;
    setErrorMessage(null);
    dispatch(setLoading(true));
    try {
      await updateUserPassword(accountId, userId, { password: values.password });
    } catch (error) {
      console.error("Failed to update password", error);
      setErrorMessage(t("generic.errors.something-went-wrong"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (!user) return <CircularProgress />;

  return (
    <Box sx={{ p: 2 }}>
      <UserProfileForm
        userData={user}
        onUpdate={handleUpdate}
        onUpdatePassword={handleUpdatePassword}
      />

      {errorMessage && (
        <Typography color="error" variant="body2" align="right" sx={{ mt: 2 }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default UserProfile;